import React from 'react';
import { AnimatedButton } from './AnimatedButton';
import { RESERVATION_URL } from '../lib/site';

interface NailServiceItem {
  name: string;
  duration: string;
  price: string;
  description?: string;
}

interface NailCategory {
  title: string;
  items: NailServiceItem[];
}

interface NailPhoto {
  src: string;
  title: string;
  label: string;
  alt: string;
}

const NAIL_PHOTOS: NailPhoto[] = [
  {
    src: '/images/dfe02848-3e2c-4cae-9ed7-492c75c91335.JPG',
    title: 'Objectif mains douces',
    label: 'Onglerie Poitiers',
    alt: 'Photo onglerie Poitiers pour la prestation objectif mains douces',
  },
  {
    src: '/images/c782fd27-02ba-4e0f-8a0c-f3cfc0193d4c.JPG',
    title: 'Pose semi-permanent',
    label: 'Onglerie Poitiers',
    alt: 'Photo onglerie Poitiers pour la pose semi-permanent',
  },
  {
    src: '/images/7f388d96-0e13-429c-8ca6-a7732b1d89b4.JPG',
    title: 'Gel X couleur',
    label: 'Onglerie Poitiers',
    alt: 'Photo onglerie Poitiers pour une pose Gel X couleur',
  },
  {
    src: '/images/5733b4a9-20f1-4913-b743-aaf07edec6ab.JPG',
    title: 'Baby Boomer',
    label: 'Onglerie Poitiers',
    alt: 'Photo onglerie Poitiers pour une pose Gel X Baby Boomer',
  },
  {
    src: '/images/5733b4a9-20f1-4913-b743-aaf07edec6ab 2.JPG',
    title: 'French manucure',
    label: 'Onglerie Poitiers',
    alt: 'Photo onglerie Poitiers pour une pose Gel X avec French',
  },
  {
    src: '/images/c299eab1-702a-4cae-b308-6df1fe802506.JPG',
    title: 'Nail art personnalisé',
    label: 'Onglerie Poitiers',
    alt: 'Photo onglerie Poitiers pour le nail art personnalisé',
  },
  {
    src: '/images/c782fd27-02ba-4e0f-8a0c-f3cfc0193d4c 2.JPG',
    title: 'Beauté du regard',
    label: 'Onglerie Poitiers',
    alt: 'Photo onglerie Poitiers pour la beauté du regard et l’épilation',
  },
];

interface NailServicesSectionProps {
  hideGallery?: boolean;
  isFullPage?: boolean;
}

const ACCENT_PINK_TEXT = '#B24D66';

export const NailServicesSection: React.FC<NailServicesSectionProps> = ({ hideGallery = false, isFullPage = false }) => {
  const HeadingTag = 'h2';
  const categories: NailCategory[] = [
    {
      title: 'Soins & Ongles',
      items: [
        { name: 'Objectif mains douce (soins des mains, nettoyage, gommage et masque)', duration: '30 min', price: '15 €' },
        { name: 'Pose semi-permanent sur ongles naturels', duration: '45 min', price: '30 €' },
        { name: 'Pose semi-permanent sur ongles naturels avec base gel de construction', duration: '1 h', price: '40 €' },
        { name: 'Pose Gel X + couleur', duration: '1 h 30', price: '50 €' },
        { name: 'Pose Gel X Baby Boomer', duration: '1 h 30', price: '55 €' },
        { name: 'Pose Gel X + French', duration: '1 h 50', price: '59 €' },
        { name: 'Remplissage gel', duration: '30 min', price: '40 €' },
        { name: 'Dépose gel (+10 € si d’un autre salon)', duration: '50 min', price: '25 €' },
      ],
    },
    {
      title: 'Nail Art & Options',
      items: [
        { name: 'Nail art personnalisé (par ongle)', duration: '50 min', price: 'à partir de 5 €' },
        { name: 'Bijoux d’ongle (strass, paillettes, autocollants)', duration: '30 min', price: 'de 5 € à 15 €' },
        { name: 'Supplément longueur – Gel X', duration: '30 min', price: '10 €' },
      ],
    },
    {
      title: 'Beauté du regard & Épilation',
      items: [
        { name: 'Dépose d’extension de cils', duration: '40 min', price: '25 €' },
        { name: 'Épilation sourcils au fil', duration: '30 min', price: 'de 17 € à 20 €' },
      ],
    },
    {
      title: 'Autre',
      items: [{ name: 'French manucure', duration: '45 min', price: '9 €' }],
    },
  ];

  return (
    <section className="relative w-full bg-white text-[#1d1d1d] py-24 px-4 md:px-12">
      {/* Subtle Pink Shapes instead of loud gradients */}
      <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-[#FEADBB]/5 rounded-full blur-[100px] -z-10 translate-x-[-20%] translate-y-[-20%]" />
      <div className="absolute bottom-[20%] right-0 w-[500px] h-[500px] bg-[#FEADBB]/5 rounded-full blur-[100px] -z-10 translate-x-[20%]" />

      <div className="max-w-[90rem] mx-auto mb-14 md:mb-16 lg:mb-20">
        <p
          className="text-[10px] sm:text-xs uppercase tracking-[0.32em] font-bold mb-4"
          style={{ color: ACCENT_PINK_TEXT }}
        >
          ESTHÉTIQUE
        </p>
        <HeadingTag className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] mb-6">
          Onglerie Poitiers
        </HeadingTag>
        <div className="max-w-5xl space-y-5 text-sm sm:text-base lg:text-lg leading-relaxed text-gray-700">
          <p>
            Soins des mains sans vernis ou avec vernis, manucure, Gel X, semi-permanent, épilation des
            sourcils au fil ou à la pince, et dépose d’extension de cils.
          </p>
          <p>
            Retrouvez notre expertise en prothésie ongulaire à Poitiers avec des prestations de haute qualité adaptées à votre style.
          </p>
        </div>
      </div>

      <div className="max-w-[90rem] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <div className="lg:col-span-4 lg:sticky lg:top-32">
          <h2 className="text-[10vw] sm:text-[9vw] lg:text-[3vw] font-black uppercase tracking-tighter leading-[0.9] mb-7 w-full">
            NOS SOINS <br /> <span style={{ color: ACCENT_PINK_TEXT }}>ONGLERIE</span>
          </h2>
          <p className="text-gray-600 text-base md:text-lg max-w-sm leading-relaxed">
            Un fond élégant, des contrastes nets et une expertise reconnue à Poitiers. 
            Réservez votre moment de détente et sublimez vos mains.
          </p>
          <div className="mt-10 mb-8 h-1.5 w-24" style={{ backgroundColor: ACCENT_PINK_TEXT }}></div>
          {!isFullPage && hideGallery && (
            <div className="mt-10">
              <AnimatedButton
                href="/ongleries-poitiers"
                dark
                className="shadow-lg border-[#FEADBB]/20"
              >
                En savoir plus
              </AnimatedButton>
            </div>
          )}
        </div>

        <div className="lg:col-span-8">
          {!hideGallery && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5 mb-16">
              {NAIL_PHOTOS.map((photo, index) => {
                const spanClass =
                  index === 0
                    ? 'sm:col-span-2 xl:col-span-2 xl:row-span-2 min-h-[24rem]'
                    : index === 2 || index === 4
                    ? 'xl:row-span-2 min-h-[18rem]'
                    : 'min-h-[18rem]';

                return (
                  <article
                    key={photo.src}
                    className={`group relative overflow-hidden rounded-[1.8rem] border border-[#1d1d1d]/10 bg-neutral-100 shadow-[0_18px_50px_rgba(15,23,42,0.08)] ${spanClass}`}
                  >
                    <img
                      src={photo.src}
                      alt={photo.alt}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                    <div className="absolute inset-x-4 bottom-4 rounded-[1.25rem] bg-white/92 px-4 py-3 text-[#1d1d1d] shadow-lg backdrop-blur-md">
                      <p
                        className="text-[10px] uppercase tracking-[0.3em] font-bold"
                        style={{ color: ACCENT_PINK_TEXT }}
                      >
                        {photo.label}
                      </p>
                      <h3 className="mt-1 text-lg sm:text-xl font-black leading-tight">{photo.title}</h3>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          <div className="flex flex-col gap-16">
            {categories.map((category, catIndex) => (
              <div key={category.title} className="flex flex-col">
                <h3 className="text-xl md:text-2xl font-bold uppercase tracking-widest text-[#1d1d1d]/70 mb-7 border-b border-[#1d1d1d]/10 pb-4">
                  0{catIndex + 1} — {category.title}
                </h3>

                <div className="flex flex-col">
                  {category.items.map((item) => (
                    <div
                      key={item.name}
                      className="group flex flex-col md:flex-row md:items-center justify-between py-6 border-b border-[#1d1d1d]/10 hover:border-[#FEADBB] transition-colors duration-300"
                    >
                      <div className="flex flex-col gap-2 md:max-w-[56%]">
                        <span className="text-xl md:text-2xl lg:text-[1.75rem] font-bold group-hover:translate-x-2 group-hover:text-[#FEADBB] transition-all duration-300">
                          {item.name}
                        </span>
                        {item.description ? (
                          <span className="text-sm text-gray-600 group-hover:translate-x-2 transition-transform duration-300">
                            {item.description}
                          </span>
                        ) : null}
                      </div>

                      <div className="flex items-center justify-between md:justify-end gap-6 md:gap-8 mt-6 md:mt-0 w-full md:w-auto">
                        <div className="text-right">
                          <div className="text-xl md:text-2xl font-bold group-hover:text-[#FEADBB] transition-colors">{item.price}</div>
                          <div className="text-xs text-gray-600 font-medium uppercase">{item.duration}</div>
                        </div>

                        <AnimatedButton
                          href={RESERVATION_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          dark
                          size="sm"
                          className="shadow-lg hover:bg-[#FEADBB] hover:border-[#FEADBB] transition-colors"
                        >
                          Réserver
                        </AnimatedButton>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
